import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard } from "../App";
import { RoleGuard } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faCommentAlt, faArrowLeft } from "@fortawesome/free-solid-svg-icons"; // Added relevant icons
import { StandardURL } from "../App";

const CommentsPage = () => {
  const [comments, setComments] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const propertyId = localStorage.getItem("selectedPropertyId");
    const token = localStorage.getItem("token");

    if (propertyId && token) {
      // Fetch comments
      axios
        .get(
          `${StandardURL}/iNyumba/admin/comments/fetch/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setComments(response.data.response);
        })
        .catch((error) => {
          console.error("Error fetching comments:", error);
        });

      // Fetch average ratings
      axios
        .get(
          `${StandardURL}/iNyumba/admin/comments/average-ratings/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setAverageRating(response.data.response);
        })
        .catch((error) => {
          console.error("Error fetching average ratings:", error);
        });
    }
  }, []);

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center">
        {[...Array(fullStars)].map((_, i) => (
          <FontAwesomeIcon key={`full-${i}`} icon={faStar} className="text-yellow-400 text-xl" />
        ))}
        {hasHalfStar && (
          <FontAwesomeIcon icon={faStar} className="text-yellow-400 text-xl opacity-50" /> // Could use faStarHalfAlt if available
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <FontAwesomeIcon key={`empty-${i}`} icon={faStar} className="text-gray-300 text-xl" />
        ))}
      </div>
    );
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-3xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200">
            {/* --- Header Section --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight flex items-center">
                Property Ratings <FontAwesomeIcon icon={faStar} className="ml-4 text-yellow-500" />
              </h1>
            </div>

            <p className="text-gray-600 mb-8 max-w-2xl mx-auto text-center">
              See what tenants are saying about the property and its overall average rating.
            </p>

            {/* --- Average Rating Section --- */}
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-6 rounded-lg shadow-lg mb-8 text-center">
              <h2 className="text-3xl font-bold mb-3 flex items-center justify-center">
                Average Rating:
                <span className="ml-3 text-4xl font-extrabold">{parseFloat(averageRating).toFixed(1)}</span>
              </h2>
              {renderStars(parseFloat(averageRating))}
              <p className="mt-2 text-sm text-yellow-100">Based on tenant feedback.</p>
            </div>

            {/* --- Comments Section --- */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-md mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4 flex items-center">
                <FontAwesomeIcon icon={faCommentAlt} className="mr-3 text-gray-600" />
                Tenant Comments
              </h2>

              <div className="space-y-4">
                {comments.length > 0 ? (
                  comments.map((comment) => (
                    <div
                      key={comment.id}
                      className="bg-white rounded-md p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
                    >
                      <p className="text-gray-800 leading-relaxed italic">"{comment.comments}"</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 italic py-4">
                    No comments available for this property yet.
                  </div>
                )}
              </div>
            </div>

            {/* --- Go Back Button --- */}
            <div className="flex justify-start mt-8">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300 ease-in-out transform hover:-translate-y-0.5"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Go Back
              </button>
            </div>
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default CommentsPage;
